import { createContext, FC, useCallback, useContext, useState } from 'react';

import { MantaCareUser, OrySession, UserContextType } from './types';

export const defaultSession: OrySession = {
    oryID: '',
    active: false,
    expiredAt: '',
    email: '',
    emailVerified: false,
    jwt: ''
};

export const defaultUserContext: UserContextType = {
    session: defaultSession,
    setUserSession: () => {
        return;
    },
    setMantaCareUser: () => {
        return;
    },
    setEmailVerified: () => {
        return;
    },
    setHasCompletedOnboardingQuestionnaire: () => {
        return;
    },
    clear: () => {
        return;
    },
    getKeyWithUserId: () => '',
    showExtendSessionDailog: false,
    setShowExtendSessionDailog: () => {
        return;
    }
};

export const UserContext = createContext<UserContextType>(defaultUserContext);

export const useUserContext = () => useContext(UserContext);

export const getKeyWithId = (id: string, key: string) => {
    return `${id}-${key}`;
};

/**
 * UserContextProvider provides user context.
 */
export const UserContextProvider: FC = ({ children }) => {
    const [session, setSession] = useState<OrySession>(defaultSession);
    const [user, setUser] = useState<MantaCareUser | undefined>();
    const [showExtendSessionDailog, setShowExtendSessionDailog] = useState(false);

    const clear = useCallback(() => {
        setUser(undefined);
        setSession(defaultSession);
    }, [setSession, setUser]);

    const getKeyWithUserId = (key: string) => {
        return user ? getKeyWithId(user.id, key) : key;
    };

    /**
     * Set user session in the state
     */
    const setUserSession = useCallback(
        (session: OrySession) => {
            setSession(session);
        },
        [setSession]
    );

    /**
     * Set email verified as true
     */
    const setEmailVerified = useCallback(() => {
        setSession(session => ({ ...session, emailVerified: true }));
    }, [setSession]);

    /**
     * Set manta care user in the the state
     */
    const setMantaCareUser = useCallback(
        (user: MantaCareUser | undefined) => {
            setUser(user);
        },
        [setUser]
    );

    /**
     * Set all question filled as true
     */
    const setHasCompletedOnboardingQuestionnaire = useCallback(() => {
        setUser(user => {
            if (user) {
                return { ...user, hasCompletedOnboardingQuestionnaire: true };
            }
        });
    }, [setUser]);

    return (
        <UserContext.Provider
            value={{
                session,
                setEmailVerified,
                setHasCompletedOnboardingQuestionnaire,
                setMantaCareUser,
                user,
                clear,
                setUserSession,
                getKeyWithUserId,
                showExtendSessionDailog,
                setShowExtendSessionDailog
            }}
        >
            {children}
        </UserContext.Provider>
    );
};
